import { forwardRef, type ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { LabelSize } from "@/types/labels";

const LABEL_SIZES = {
  small: { width: 50, height: 25, qrSize: 40, fontSize: 6 },
  medium: { width: 100, height: 50, qrSize: 80, fontSize: 8 },
  large: { width: 150, height: 100, qrSize: 120, fontSize: 10 },
} as const;

const BASE_URL = window.location.origin;

interface QRLabelBaseProps {
  shortCode: string;
  size?: LabelSize;
  showLogo?: boolean;
  showISO?: boolean;
  title?: string;
  subtitle?: string;
  children: ReactNode;
}

export const QRLabelBase = forwardRef<HTMLDivElement, QRLabelBaseProps>(
  ({ shortCode, size = "medium", showLogo = true, showISO = true, title, subtitle, children }, ref) => {
    const dimensions = LABEL_SIZES[size];
    const qrUrl = `${BASE_URL}/t/${shortCode}`;

    return (
      <div ref={ref} className="qr-label-container">
        <style>
          {`
            @media print {
              @page {
                size: ${dimensions.width}mm ${dimensions.height}mm;
                margin: 0;
              }
              body { margin: 0; padding: 0; }
              .qr-label-container { page-break-after: always; }
              .no-print { display: none !important; }
            }
          `}
        </style>

        <div
          style={{
            width: `${dimensions.width}mm`,
            height: `${dimensions.height}mm`,
            padding: "2mm",
            display: "flex",
            gap: "2mm",
            border: "1px solid #e5e7eb",
            fontFamily: "'Segoe UI', Arial, sans-serif",
            backgroundColor: "white",
            boxSizing: "border-box",
          }}
        >
          {/* Left: QR Code */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: `${dimensions.qrSize + 4}px`,
            }}
          >
            <QRCodeSVG
              value={qrUrl}
              size={dimensions.qrSize}
              level="H"
              includeMargin={false}
            />
            <div
              style={{
                fontSize: `${dimensions.fontSize - 1}pt`,
                fontFamily: "monospace",
                marginTop: "1mm",
                fontWeight: "bold",
              }}
            >
              {shortCode}
            </div>
          </div>

          {/* Right: Content */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              fontSize: `${dimensions.fontSize}pt`,
            }}
          >
            {/* Header with logos */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid #1a365d",
                paddingBottom: "1mm",
                marginBottom: "1mm",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "2mm" }}>
                {showLogo && (
                  <img
                    src="/perseo/logoMagma.png"
                    alt="Magma"
                    style={{ height: size === "small" ? "4mm" : "6mm", objectFit: "contain" }}
                    crossOrigin="anonymous"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                )}
                {showISO && (
                  <img
                    src="/perseo/logoISO.png"
                    alt="ISO"
                    style={{ height: size === "small" ? "3mm" : "5mm", objectFit: "contain" }}
                    crossOrigin="anonymous"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                )}
              </div>
              {title && (
                <div
                  style={{
                    fontSize: `${dimensions.fontSize + 2}pt`,
                    fontWeight: "700",
                    color: "#1a365d",
                  }}
                >
                  {title}
                </div>
              )}
            </div>

            {/* Subtitle */}
            {subtitle && (
              <div
                style={{
                  fontSize: `${dimensions.fontSize + 1}pt`,
                  fontWeight: "600",
                  color: "#2d4a6f",
                  marginBottom: "1mm",
                }}
              >
                {subtitle}
              </div>
            )}

            {/* Custom content */}
            <div style={{ flex: 1, overflow: "hidden" }}>
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

QRLabelBase.displayName = "QRLabelBase";

// Helper component for label rows
interface LabelRowProps {
  label: string;
  value: string | number | undefined;
  highlight?: boolean;
}

export const LabelRow = ({ label, value, highlight }: LabelRowProps) => {
  if (value === undefined || value === null || value === "") return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "0.5mm",
        ...(highlight && { fontWeight: "bold", color: "#1a365d" }),
      }}
    >
      <span style={{ color: "#4a5568" }}>{label}:</span>
      <span style={{ fontWeight: "500" }}>{value}</span>
    </div>
  );
};

// Helper component for material list
interface MaterialListProps {
  materials: Array<{ n: string; l: number; g?: number }>;
  compact?: boolean;
}

export const MaterialList = ({ materials, compact }: MaterialListProps) => {
  if (!materials || materials.length === 0) return null;

  if (compact) {
    return (
      <div style={{ fontSize: "inherit" }}>
        {materials.map((m, i) => (
          <span key={i}>
            {m.n} ({m.l}){i < materials.length - 1 ? ", " : ""}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div style={{ fontSize: "inherit" }}>
      {materials.map((m, i) => (
        <div key={i} style={{ display: "flex", gap: "2mm" }}>
          <span>{m.n}</span>
          <span style={{ color: "#4a5568" }}>x{m.l}</span>
          {m.g && <span style={{ color: "#718096" }}>{m.g}gsm</span>}
        </div>
      ))}
    </div>
  );
};
